import { v4 as uuidv4 } from 'uuid';
import {
  Criterion,
  CriterionListItem,
  FieldChoice,
  ListValueType,
  SelectionBehavior,
} from 'queries';
import { ICriterionHandler } from './ICriterionHandler';
import { ParticipantField, ParticipantFieldType } from '@app/api';

export class KeywordCriterionHandler extends ICriterionHandler {
  handleCriterion(criterion: Criterion) {
    return criterion.listValueType === ListValueType.KEYWORD;
  }

  criterionHandleOthers() {
    return true;
  }

  private removeFromAllList(criterion: Criterion, choice: FieldChoice) {
    const { ref } = choice;
    const denyList = criterion.denyList.filter((el) => el !== ref);

    const allowList = criterion.allowList
      ? criterion.allowList.filter((el) => el !== ref)
      : undefined;

    const automaticlyAcceptedList = (
      criterion.automaticlyAcceptedList || []
    ).filter((el) => el !== ref);

    return {
      ...criterion,
      allowList,
      denyList,
      automaticlyAcceptedList,
    };
  }

  declineChoice(criterion: Criterion, choice: FieldChoice): Criterion {
    const newCriterion = this.removeFromAllList(criterion, choice);
    newCriterion.denyList.push(choice.ref);
    return newCriterion;
  }

  acceptChoice(criterion: Criterion, choice: FieldChoice): Criterion {
    const newCriterion = this.removeFromAllList(criterion, choice);
    if (newCriterion.allowList) {
      newCriterion.allowList.push(choice.ref);
    }
    return newCriterion;
  }

  tolerateChoice(criterion: Criterion, choice: FieldChoice): Criterion {
    const newCriterion = this.removeFromAllList(criterion, choice);
    if (!newCriterion.allowList) {
      throw new Error("Can't tolerate without allowList");
    }
    return newCriterion;
  }

  automaticallyAcceptChoice(
    criterion: Criterion,
    choice: FieldChoice,
  ): Criterion {
    const newCriterion = this.acceptChoice(criterion, choice);
    if (!newCriterion.automaticlyAcceptedList) {
      newCriterion.automaticlyAcceptedList = [];
    }
    newCriterion.automaticlyAcceptedList?.push(choice.ref);
    return newCriterion;
  }

  removeChoice(criterion: Criterion, choice: FieldChoice): Criterion {
    return this.removeFromAllList(criterion, choice);
  }

  handleParticipantField(field: ParticipantField): boolean {
    return [
      ParticipantFieldType.SINGLE_CHOICE,
      ParticipantFieldType.MULTIPLE_CHOICE,
      ParticipantFieldType.INCOME,
    ].includes(field.type);
  }

  createEmptyCriterionFromParticipantField(field: ParticipantField): Criterion {
    return {
      id: uuidv4(),
      fieldRef: field.name,
      screenerCriterion: false,
      selectionBehavior: SelectionBehavior.REQUIRED,
      listValueType: ListValueType.KEYWORD,
      denyList: [],
      allowList: null,
    };
  }

  setCriterionValues(
    criterion: Criterion,
    values?: CriterionListItem[],
  ): Criterion {
    if (!values) {
      return {
        ...criterion,
        allowList: null,
        denyList: [],
        automaticlyAcceptedList: [],
      };
    }

    const allowList = values.map(String);
    return {
      ...criterion,
      allowList,
      denyList: [],
      automaticlyAcceptedList: (criterion.automaticlyAcceptedList || []).filter(
        (v) => allowList.includes(String(v)),
      ),
    };
  }

  protected compareValues(item: CriterionListItem, choice: FieldChoice) {
    if (typeof item !== 'string') {
      return false;
    }

    return super.compareValues(item, choice);
  }
}
